import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, CardActionArea, Typography, Button, Grid,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Stack, CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add, SearchNormal1, People } from 'iconsax-react';
import { useTeamStore } from '../../store/team.store';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';
import { Team } from '../../types/team.type';

const TEAM_COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6'];

function TeamCard({ team }: { team: Team }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Card sx={{ height: '100%', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 30px rgba(0,0,0,0.1)' } }}>
      <CardActionArea onClick={() => navigate(`/dashboard/teams/${team.id}`)} sx={{ height: '100%' }}>
        <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: 2.5, flexShrink: 0,
                bgcolor: `${team.color}18`,
                border: `1px solid ${team.color}30`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <People size={20} color={team.color} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>{team.name}</Typography>
              {team.description && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{team.description}</Typography>
              )}
            </Box>
          </Box>

          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 'auto' }}>
            {new Date(team.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function TeamsPage() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { teams, isLoading, fetchTeams, addTeam } = useTeamStore();
  const [search, setSearch]     = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm]         = useState({ name: '', description: '', color: TEAM_COLORS[0] });
  const [saving, setSaving]     = useState(false);

  useEffect(() => { 
    fetchTeams(); 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = teams.filter(team =>
    team.name.toLowerCase().includes(search.toLowerCase()) ||
    (team.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await addTeam({ name: form.name, description: form.description || null, color: form.color });
      setForm({ name: '', description: '', color: TEAM_COLORS[0] });
      setCreateOpen(false);
      showToast(t('teams.createdSuccess') || 'Team created!', TOAST_TYPES.SUCCESS);
    } catch {
      showToast(t('common.error') || 'Error', TOAST_TYPES.ERROR);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.3px' }}>
            {t('teams.title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.5 }}>
            {t('teams.subtitleSimple', { count: teams.length })}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add size={18} color="#fff" />} onClick={() => setCreateOpen(true)}>
          {t('teams.createTeam')}
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          size="small"
          placeholder={t('teams.searchPlaceholder')}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchNormal1 size={16} color="#94A3B8" /></InputAdornment> }}
          sx={{ width: 280 }}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Typography sx={{ color: 'text.secondary' }}>{t('teams.noTeams')}</Typography>
          <Button variant="contained" startIcon={<Add size={16} color="#fff" />} onClick={() => setCreateOpen(true)} sx={{ mt: 2 }}>
            {t('teams.createTeam')}
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filtered.map(team => (
            <Grid item xs={12} sm={6} lg={4} key={team.id}>
              <TeamCard team={team} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Team Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('teams.createTeam')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
                {t('teams.teamName')} <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField 
                fullWidth 
                size="small" 
                value={form.name} 
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder={t('teams.teamName')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
                  },
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
                {t('teams.description')}
              </Typography>
              <TextField 
                fullWidth 
                size="small" 
                multiline 
                rows={2} 
                value={form.description} 
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder={t('teams.description')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
                  },
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 1 }}>{t('teams.color') || 'Color'}</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {TEAM_COLORS.map(c => (
                  <Box
                    key={c} onClick={() => setForm(p => ({ ...p, color: c }))}
                    sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer', border: form.color === c ? `3px solid ${c}` : '3px solid transparent', outline: form.color === c ? `2px solid #fff` : 'none', transition: 'all 0.1s' }}
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: 'text.secondary' }}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.name.trim() || saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Add size={16} color="#fff" />}>
            {t('teams.createTeam')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
